import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styleSheets/managementPanel.css";
import WelcomeHeader from "./WelcomeHeader";
import { DataTable, ColumnDef } from "./DataTable";

import { useUser } from "../contexts/UserContext";
import { Recipient, Attachment, DeliveredMessages } from "../API";
import type {
  DeliveredMessageListItem,
  LegacyMessageListItem,
} from "../types/message";

import { capitalizeFirst, convertUtcToLocal } from "../utilities/utilities";
import {
  useLegacyMessages,
  useRecipients,
  useAttachments,
  useDeliveredMessages,
  useDeliveredMessagesBySender,
  useEffectivePlatformLimits,
  useUserPlan,
} from "../graphql/hooks";
import { getAttachmentAddEligibility } from "../utilities/platformLimits";

/** Tabs supported by this page */
export type TabKey =
  | "legacyMessages"
  | "recipients"
  | "files"
  | "inheritedMessages"
  | "deliveredMessages";

const DEFAULT_TAB: TabKey = "legacyMessages";
const EMPTY_VALUE = "—";

function isTabKey(v: string | null): v is TabKey {
  return (
    v === "legacyMessages" ||
    v === "recipients" ||
    v === "files" ||
    v === "inheritedMessages" ||
    v === "deliveredMessages"
  );
}

function ManagementPanel() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [params, setParams] = useSearchParams();
  const { user, isAuthenticated } = useUser();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/", { state: { message: t("common.signIn") } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  // Read initial tab from URL (?selectedTab=files, etc.)
  const tabParam = params.get("selectedTab");
  const initialTab: TabKey = isTabKey(tabParam) ? tabParam : DEFAULT_TAB;

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // Keep state in sync if URL changes while staying on the page
  useEffect(() => {
    setActiveTab(isTabKey(tabParam) ? tabParam : DEFAULT_TAB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const userId = user?.userId ?? "";
  const loggedInEmail = (user?.signInDetails?.loginId ?? "")
    .trim()
    .toLowerCase();

  // ✅ Read from TanStack cache (or fetch if stale/missing)
  const legacyQ = useLegacyMessages(userId, isAuthenticated && !!userId);
  const recipientsQ = useRecipients(userId, isAuthenticated && !!userId);
  const attachmentsQ = useAttachments(userId, isAuthenticated && !!userId);
  const deliveredQ = useDeliveredMessages(
    loggedInEmail,
    isAuthenticated && !!userId && !!loggedInEmail,
  );
  const senderDeliveredQ = useDeliveredMessagesBySender(
    userId,
    isAuthenticated && !!userId,
  );
  const planQ = useUserPlan(userId, isAuthenticated && !!userId);
  const effectivePlatformLimitsQ = useEffectivePlatformLimits(
    userId,
    isAuthenticated && !!userId,
  );
  const userPlan = planQ.data;
  const canCreateMessages =
    Number(userPlan?.messagesAllowed ?? 0) > Number(legacyQ?.data?.length ?? 0);
  const canCreateRecipients =
    Number(userPlan?.recipientsAllowed ?? 0) >
    Number(recipientsQ?.data?.length ?? 0);
  const attachmentAddEligibility = getAttachmentAddEligibility({
    attachmentsAllowed: userPlan?.attachmentsAllowed,
    currentAttachmentCount: attachmentsQ.data?.length ?? 0,
    maxStorageBytesPerUser:
      effectivePlatformLimitsQ.data?.maxStorageBytesPerUser,
    storageUsedBytes: effectivePlatformLimitsQ.data?.storageUsedBytes,
    isPlanLoading: planQ.isLoading,
    isEffectivePlatformLimitsLoading: effectivePlatformLimitsQ.isLoading,
  });
  const attachmentAddDisabledTooltip =
    attachmentAddEligibility.reason === "loading"
      ? t("common.loading")
      : attachmentAddEligibility.reason === "plan_attachment_count_reached"
        ? t("file.errorMessage.planLimitReached")
        : attachmentAddEligibility.reason === "platform_storage_reached"
          ? t("file.errorMessage.storageLimitReached")
          : undefined;

  if (!isAuthenticated) {
    navigate("/", { state: { message: t("common.signIn") } });
  }

  // Change tab + update URL
  const goTab = (tab: TabKey) => {
    setActiveTab(tab);
    setParams({ selectedTab: tab });
  };

  const openMessage = (msg: LegacyMessageListItem) =>
    navigate("/LegacyMessageView/", { state: msg });
  const openRecipient = (r: Recipient) =>
    navigate("/RecipientView/", { state: r });
  const openAttachment = (a: Attachment) =>
    navigate("/AttachmentView/", { state: a });
  const openDelivered = (m: DeliveredMessageListItem) => {
    if (!m.id) return;
    navigate(`/InheritedMessageView/${m.id}`, { state: m });
  };
  const openDeliveredMessage = (m: DeliveredMessages) => {
    if (!m.id) return;
    navigate(`/delivered-messages/${m.id}`, { state: m });
  };

  function formatValue(
    value: string | number | boolean | null | undefined,
  ): string {
    if (typeof value === "boolean") {
      return value ? t("common.yes") : t("common.no");
    }
    if (typeof value === "number") {
      return value.toString();
    }
    const text = String(value ?? "").trim();
    return text.length > 0 ? text : EMPTY_VALUE;
  }

  function formatDateValue(value: string | null | undefined): string {
    const local = convertUtcToLocal(value ?? null, "NoDay");
    return local || EMPTY_VALUE;
  }

  function formatMessageRead(value: boolean | null | undefined): string {
    if (value === true) return t("common.yes");
    if (value === false) return t("common.no");
    return EMPTY_VALUE;
  }

  // ----------------------------
  // Column definitions (MINIMAL)
  // ----------------------------

  const legacyMessageColumns: ColumnDef<LegacyMessageListItem>[] = useMemo(
    () => [
      {
        id: "title",
        header: t("message.tableColumn.title"),
        cell: (m) => m.title ?? "",
        sortValue: (m) => m.title ?? "",
        searchValue: (m) => m.title ?? "",
        width: "30%",
      },
      {
        id: "scheduledDate",
        header: t("message.tableColumn.scheduledDate"),
        cell: (m) => convertUtcToLocal(m.scheduledDate, "ShowDayShort"),
        sortValue: (m) => m.scheduledDate ?? "",
        width: "16%",
      },
      {
        id: "eol",
        header: t("message.tableColumn.eol"),
        cell: (m) => (m.endOfLifeTrigger ? t("common.yes") : t("common.no")),
        align: "center",
        width: "8%",
        sortValue: (m) =>
          m.endOfLifeTrigger ? t("common.yes") : t("common.no"),
      },
      {
        id: "size",
        header: t("message.tableColumn.size"),
        cell: (m) => (m.size ?? "").toString(),
        align: "right",
        width: "10%",
      },
      {
        id: "recipients",
        header: t("message.tableColumn.recipients"),
        cell: (m) =>
          (
            (m.Recipients?.items?.filter(Boolean).length ?? 0) as number
          ).toString(),
        align: "center",
        width: "10%",
      },
      {
        id: "attachments",
        header: capitalizeFirst(t("file.attachments")),
        cell: (m) =>
          (
            (m.Attachments?.items?.filter(Boolean).length ?? 0) as number
          ).toString(),
        align: "center",
        width: "12%",
      },
      {
        id: "sentOn",
        header: t("message.tableColumn.sentDate"),
        cell: (m) => convertUtcToLocal((m as any).sentDate ?? null, "NoDay"),
        sortValue: (m) => ((m as any).sentDate ?? "") as string,
        width: "14%",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const recipientColumns: ColumnDef<Recipient>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("recipient.tableColumn.name"),
        cell: (r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim(),
        sortValue: (r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim(),
        searchValue: (r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim(),
        width: "30%",
      },
      {
        id: "email",
        header: t("recipient.tableColumn.email"),
        cell: (r) => r.email ?? "",
        sortValue: (r) => r.email ?? "",
        searchValue: (r) => r.email ?? "",
        width: "30%",
      },
      {
        id: "phone",
        header: t("recipient.tableColumn.phone"),
        cell: (r) => r.phone ?? "",
        searchValue: (r) => r.phone ?? "",
        width: "20%",
      },
      {
        id: "inMessages",
        header: t("recipient.tableColumn.inMessages"),
        cell: (r) =>
          (
            ((r as any).messages?.items?.filter((x: any) => x?.message)
              .length ?? 0) as number
          ).toString(),
        align: "center",
        width: "20%",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const attachmentColumns: ColumnDef<Attachment>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("file.tableColumn.name"),
        cell: (a) => a.name ?? "",
        sortValue: (a) => a.name ?? "",
        searchValue: (a) => a.name ?? "",
        width: "22%",
      },
      {
        id: "description",
        header: t("file.tableColumn.description"),
        cell: (a) => a.description ?? "",
        searchValue: (a) => a.description ?? "",
        width: "30%",
      },
      {
        id: "type",
        header: t("file.tableColumn.type"),
        cell: (a) => a.type ?? "",
        sortValue: (a) => a.type ?? "",
        searchValue: (a) => a.type ?? "",
        width: "16%",
      },
      {
        id: "size",
        header: t("file.tableColumn.size"),
        cell: (a) => (a.size ?? "").toString(),
        align: "right",
        width: "12%",
      },
      {
        id: "inMessages",
        header: t("file.tableColumn.inMessages"),
        cell: (a) =>
          (
            ((a as any).messages?.items?.filter((x: any) => x?.message)
              .length ?? 0) as number
          ).toString(),
        align: "center",
        width: "20%",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Delivered/inherited messages -> Delivered On, From, Title
  const deliveredColumns: ColumnDef<DeliveredMessageListItem>[] = useMemo(
    () => [
      {
        id: "deliveredOn",
        header: t("inheritedMessage.tableColumn.deliveredOn"),
        cell: (m) => convertUtcToLocal(m.deliveredAt ?? null, "NoDay"),
        sortValue: (m) => m.deliveredAt ?? "",
        width: "20%",
      },
      {
        id: "from",
        header: t("inheritedMessage.tableColumn.from"),
        cell: (m) => m.fromName ?? "",
        sortValue: (m) => m.fromName ?? "",
        searchValue: (m) => m.fromName ?? "",
        width: "25%",
      },
      {
        id: "title",
        header: t("inheritedMessage.tableColumn.messageTitle"),
        cell: (m) => m.title ?? "",
        sortValue: (m) => m.title ?? "",
        searchValue: (m) => m.title ?? "",
      },
      {
        id: "attachments",
        header: capitalizeFirst(t("file.attachments")),
        cell: (m) => ((m.attachmentCount ?? 0) as number).toString(),
        align: "center",
        width: "12%",
      },
      {
        id: "read",
        header: t("inheritedMessage.tableColumn.messageRead"),
        cell: (m) => (m.messageRead ? t("common.yes") : t("common.no")),
        align: "center",
        width: "8%",
        sortValue: (m) => (m.messageRead ? t("common.yes") : t("common.no")),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deliveredSenderColumns: ColumnDef<DeliveredMessages>[] = useMemo(
    () => [
      {
        id: "recipientEmail",
        header: t("deliveredMessage.tableColumn.recipientEmail"),
        cell: (m) => formatValue(m.recipientEmail),
        sortValue: (m) => String(m.recipientEmail ?? ""),
        searchValue: (m) => String(m.recipientEmail ?? ""),
        width: "16%",
      },
      {
        id: "title",
        header: t("deliveredMessage.tableColumn.title"),
        cell: (m) => formatValue(m.title),
        sortValue: (m) => String(m.title ?? ""),
        searchValue: (m) => String(m.title ?? ""),
        width: "16%",
      },
      {
        id: "status",
        header: t("deliveredMessage.tableColumn.status"),
        cell: (m) => formatValue(m.status),
        sortValue: (m) => String(m.status ?? ""),
        searchValue: (m) => String(m.status ?? ""),
        width: "10%",
      },
      {
        id: "deliveredAt",
        header: t("deliveredMessage.tableColumn.deliveredAt"),
        cell: (m) => formatDateValue(m.deliveredAt),
        sortValue: (m) => String(m.deliveredAt ?? ""),
        width: "14%",
      },
      {
        id: "deliveryType",
        header: t("deliveredMessage.tableColumn.deliveryType"),
        cell: (m) => formatValue(m.deliveryType),
        sortValue: (m) => String(m.deliveryType ?? ""),
        searchValue: (m) => String(m.deliveryType ?? ""),
        width: "10%",
      },
      {
        id: "messageRead",
        header: t("deliveredMessage.tableColumn.messageRead"),
        cell: (m) => formatMessageRead(m.messageRead),
        sortValue: (m) => formatMessageRead(m.messageRead),
        align: "center",
        width: "8%",
      },
      {
        id: "attemptCount",
        header: t("deliveredMessage.tableColumn.attemptCount"),
        cell: (m) => formatValue(m.attemptCount),
        sortValue: (m) => String(m.attemptCount ?? ""),
        align: "center",
        width: "8%",
      },
      {
        id: "lastAttemptAt",
        header: t("deliveredMessage.tableColumn.lastAttemptAt"),
        cell: (m) => formatDateValue(m.lastAttemptAt),
        sortValue: (m) => String(m.lastAttemptAt ?? ""),
        width: "14%",
      },
      {
        id: "statusUpdatedAt",
        header: t("deliveredMessage.tableColumn.statusUpdatedAt"),
        cell: (m) => formatDateValue(m.statusUpdatedAt),
        sortValue: (m) => String(m.statusUpdatedAt ?? ""),
        width: "14%",
      },
      {
        id: "readReminderCount",
        header: t("deliveredMessage.tableColumn.readReminderCount"),
        cell: (m) => formatValue(m.readReminderCount),
        sortValue: (m) => String(m.readReminderCount ?? ""),
        align: "center",
        width: "8%",
      },
      {
        id: "readReminderStatus",
        header: t("deliveredMessage.tableColumn.readReminderStatus"),
        cell: (m) => formatValue(m.readReminderStatus),
        sortValue: (m) => String(m.readReminderStatus ?? ""),
        searchValue: (m) => String(m.readReminderStatus ?? ""),
        width: "10%",
      },
      {
        id: "failureReason",
        header: t("deliveredMessage.tableColumn.failureReason"),
        cell: (m) => formatValue(m.failureReason),
        sortValue: (m) => String(m.failureReason ?? ""),
        searchValue: (m) => String(m.failureReason ?? ""),
        width: "16%",
      },
      {
        id: "attachmentCount",
        header: t("deliveredMessage.tableColumn.attachmentCount"),
        cell: (m) => formatValue(m.attachmentCount),
        sortValue: (m) => String(m.attachmentCount ?? ""),
        align: "center",
        width: "8%",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Active tab data + loading/error flags
  const tabState = useMemo(() => {
    switch (activeTab) {
      case "legacyMessages":
        return {
          data: (legacyQ.data ?? []) as LegacyMessageListItem[],
          isLoading: legacyQ.isLoading,
          isError: legacyQ.isError,
          error: legacyQ.error,
        };
      case "recipients":
        return {
          data: recipientsQ.data ?? [],
          isLoading: recipientsQ.isLoading,
          isError: recipientsQ.isError,
          error: recipientsQ.error,
        };
      case "files":
        return {
          data: attachmentsQ.data ?? [],
          isLoading: attachmentsQ.isLoading,
          isError: attachmentsQ.isError,
          error: attachmentsQ.error,
        };
      case "inheritedMessages":
        return {
          data: deliveredQ.data ?? [],
          isLoading: deliveredQ.isLoading,
          isError: deliveredQ.isError,
          error: deliveredQ.error,
        };
      case "deliveredMessages":
        return {
          data: senderDeliveredQ.data ?? [],
          isLoading: senderDeliveredQ.isLoading,
          isError: senderDeliveredQ.isError,
          error: senderDeliveredQ.error,
        };
      default:
        return {
          data: [],
          isLoading: false,
          isError: false,
          error: undefined as any,
        };
    }
  }, [
    activeTab,
    legacyQ.data,
    legacyQ.isLoading,
    legacyQ.isError,
    legacyQ.error,
    recipientsQ.data,
    recipientsQ.isLoading,
    recipientsQ.isError,
    recipientsQ.error,
    attachmentsQ.data,
    attachmentsQ.isLoading,
    attachmentsQ.isError,
    attachmentsQ.error,
    deliveredQ.data,
    deliveredQ.isLoading,
    deliveredQ.isError,
    deliveredQ.error,
    senderDeliveredQ.data,
    senderDeliveredQ.isLoading,
    senderDeliveredQ.isError,
    senderDeliveredQ.error,
  ]);

  return (
    <>
      <section className="management-section">
        <section className="management-top">
          <WelcomeHeader variant="panel" />
          <section className="management-top-buttons">
            <button
              className={`tab-btn ${activeTab === "legacyMessages" ? "tab-btn--on" : "tab-btn--off"}`}
              type="button"
              onClick={() => goTab("legacyMessages")}
            >
              <div className="tab-btn__content">
                <div className="icon-container">
                  <div className="tab-btn__icon legacy-messages-icon" />
                </div>
                <div className="tab-btn__label">
                  {t("message.myLegacyMessages")}
                </div>
              </div>
              <div className="tab-btn__indicator"></div>
            </button>

            <button
              className={`tab-btn ${activeTab === "recipients" ? "tab-btn--on" : "tab-btn--off"}`}
              type="button"
              onClick={() => goTab("recipients")}
            >
              <div className="tab-btn__content">
                <div className={"icon-container"}>
                  <div className="tab-btn__icon recipients-icon" />
                </div>
                <div className="tab-btn__label">
                  {t("recipient.myRecipients")}
                </div>
              </div>
              <div className="tab-btn__indicator"></div>
            </button>

            <button
              className={`tab-btn ${activeTab === "files" ? "tab-btn--on" : "tab-btn--off"}`}
              type="button"
              onClick={() => goTab("files")}
            >
              <div className="tab-btn__content">
                <div className={"icon-container"}>
                  <div className="tab-btn__icon files-icon" />
                </div>
                <div className="tab-btn__label">{t("file.myFiles")}</div>
              </div>
              <div className="tab-btn__indicator"></div>
            </button>

            <button
              className={`tab-btn ${activeTab === "inheritedMessages" ? "tab-btn--on" : "tab-btn--off"}`}
              type="button"
              onClick={() => goTab("inheritedMessages")}
            >
              <div className="tab-btn__content">
                <div className={"icon-container"}>
                  <div className="tab-btn__icon inherited-icon" />
                </div>
                <div className="tab-btn__label">
                  {t("inheritedMessage.inheritedMessages")}
                </div>
              </div>
              <div className="tab-btn__indicator"></div>
            </button>

            <button
              className={`tab-btn ${activeTab === "deliveredMessages" ? "tab-btn--on" : "tab-btn--off"}`}
              type="button"
              onClick={() => goTab("deliveredMessages")}
            >
              <div className="tab-btn__content">
                <div className={"icon-container"}>
                  <div className="tab-btn__icon delivered-messages-icon" />
                </div>
                <div className="tab-btn__label">
                  {t("deliveredMessage.deliveredMessages")}
                </div>
              </div>
              <div className="tab-btn__indicator"></div>
            </button>
          </section>
        </section>

        <section className="management-bottom">
          <section className="management-table">
            {tabState.isLoading && (
              <div className="management-loading">Loading…</div>
            )}

            {tabState.isError && !tabState.isLoading && (
              <div className="management-loading" style={{ color: "#ef4130" }}>
                Failed to load data.
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
                  {tabState.error?.message ?? "Unknown error"}
                </div>
              </div>
            )}

            {!tabState.isLoading &&
              !tabState.isError &&
              activeTab === "legacyMessages" && (
                <DataTable<LegacyMessageListItem>
                  data={tabState.data as LegacyMessageListItem[]}
                  columns={legacyMessageColumns}
                  getRowKey={(row, _idx) => row.id}
                  onRowClick={openMessage}
                  enableSearch={true}
                  enableAddNew={true}
                  addNewLabel={"+ " + t("message.newMessage")}
                  addNewLink="/MessageEdit"
                  disableAddNew={!canCreateMessages}
                  disableAddNewTooltip={t(
                    "message.errorMessage.planLimitReached",
                  )}
                  searchPlaceholder={t("message.searchMessages")}
                  initialPageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  emptyTitle={t("message.onScreenMessage.noLegacyMessages")}
                  emptyDescription={t("message.onScreenMessage.createFirst")}
                />
              )}

            {!tabState.isLoading &&
              !tabState.isError &&
              activeTab === "recipients" && (
                <DataTable<Recipient>
                  data={tabState.data as Recipient[]}
                  columns={recipientColumns}
                  getRowKey={(row, _idx) => row.id}
                  onRowClick={openRecipient}
                  enableSearch={true}
                  enableAddNew={true}
                  addNewLabel={"+ " + t("recipient.newRecipient")}
                  addNewLink="/RecipientEdit"
                  disableAddNew={!canCreateRecipients}
                  disableAddNewTooltip={t(
                    "recipient.errorMessage.planLimitReached",
                  )}
                  searchPlaceholder={t("recipient.searchRecipient")}
                  initialPageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  emptyTitle={t("recipient.onScreenMessage.noRecipients")}
                  emptyDescription={t("recipient.onScreenMessage.createFirst")}
                />
              )}

            {!tabState.isLoading &&
              !tabState.isError &&
              activeTab === "files" && (
                <DataTable<Attachment>
                  data={tabState.data as Attachment[]}
                  columns={attachmentColumns}
                  getRowKey={(row, _idx) => row.id}
                  onRowClick={openAttachment}
                  enableSearch={true}
                  enableAddNew={true}
                  addNewLabel={"+ " + t("file.newFile")}
                  addNewLink="/AttachmentEdit"
                  disableAddNew={!attachmentAddEligibility.canAddAttachment}
                  disableAddNewTooltip={attachmentAddDisabledTooltip}
                  searchPlaceholder={t("file.searchFiles")}
                  initialPageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  emptyTitle={t("file.onScreenMessage.noFiles")}
                  emptyDescription={t("file.onScreenMessage.createFirst")}
                />
              )}

            {!tabState.isLoading &&
              !tabState.isError &&
              activeTab === "inheritedMessages" && (
                <DataTable<DeliveredMessageListItem>
                  data={tabState.data as DeliveredMessageListItem[]}
                  columns={deliveredColumns}
                  getRowKey={(row, idx) => row.id ?? String(idx)}
                  onRowClick={openDelivered}
                  enableSearch={true}
                  enableAddNew={false}
                  addNewLabel=""
                  addNewLink=""
                  disableAddNew={false}
                  disableAddNewTooltip=""
                  searchPlaceholder={t("inheritedMessage.searchInherited")}
                  initialPageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  emptyTitle={t("inheritedMessage.onScreenMessage.noInherited")}
                  emptyDescription={t(
                    "inheritedMessage.onScreenMessage.inheritedHere",
                  )}
                />
              )}

            {!tabState.isLoading &&
              !tabState.isError &&
              activeTab === "deliveredMessages" && (
                <DataTable<DeliveredMessages>
                  data={tabState.data as DeliveredMessages[]}
                  columns={deliveredSenderColumns}
                  getRowKey={(row, idx) => row.id ?? String(idx)}
                  onRowClick={openDeliveredMessage}
                  enableSearch={true}
                  enableAddNew={false}
                  addNewLabel=""
                  addNewLink=""
                  disableAddNew={false}
                  disableAddNewTooltip=""
                  searchPlaceholder={t("deliveredMessage.searchDelivered")}
                  initialPageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  emptyTitle={t("deliveredMessage.onScreenMessage.noDelivered")}
                  emptyDescription={t(
                    "deliveredMessage.onScreenMessage.deliveredHere",
                  )}
                />
              )}
          </section>
        </section>
      </section>
    </>
  );
}

export default ManagementPanel;
